import { useEffect, useRef, type ReactNode } from "react";

/**
 * The sign-in screen's frame: a dark watchtower panel with a living network
 * on one side, the form on the other.
 *
 * The network is decoration with the product's own vocabulary: shards on the
 * left, hops through the middle, the services they watch on the right, and
 * pulses of light travelling the paths like traceroutes. Now and then one
 * fails at a hop and turns red, because that is the thing Heimdall exists to
 * notice. Nothing here is real data. It stands still for anyone who has asked
 * their system for reduced motion.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <aside className="auth-stage">
        <div className="auth-hero">
          <img
            className="auth-logo"
            src="/assets/heimdall-logo.jpg"
            alt="Heimdall: network vision and control"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/assets/heimdall-logo.svg";
            }}
          />
          <div className="auth-tagline">See everything.</div>
        </div>
        <div className="auth-network" aria-hidden="true">
          <NetworkCanvas />
        </div>
        <ul className="auth-legend" aria-hidden="true">
          <li><i className="shard" />Shards</li>
          <li><i className="hop" />Hops</li>
          <li><i className="service" />Services</li>
        </ul>
      </aside>
      <main className="auth-panel">{children}</main>
    </div>
  );
}

type Node = { x: number; y: number; kind: "shard" | "hop" | "service"; glow: number; bad: number };
type Edge = { a: number; b: number; c1x: number; c1y: number; c2x: number; c2y: number };
type Pulse = { route: number[]; at: number; speed: number; fails: number; hue: "ok" | "bad" };

const COLORS = {
  ink: "rgba(203, 221, 245, 0.55)",
  edge: "rgba(120, 160, 210, 0.16)",
  shard: "#7dd3fc",
  hop: "#b8a9e8",
  service: "#a7f3d0",
  pulse: "#38bdf8",
  bad: "#f87171",
};

/** A layered graph: 3 shards → 3 hop layers → 3 services, with crossings. */
function build(w: number, h: number) {
  const layers = [3, 4, 5, 4, 3];
  const nodes: Node[] = [];
  const index: number[][] = [];
  const left = w * 0.08, right = w * 0.92, top = h * 0.08, bottom = h * 0.92;
  layers.forEach((count, li) => {
    index.push([]);
    for (let i = 0; i < count; i++) {
      const jitter = Math.sin((li + 1) * 12.9898 + i * 78.233) * 0.5;
      const x = left + ((right - left) * li) / (layers.length - 1) + jitter * w * 0.02;
      const y = top + ((bottom - top) * (i + 0.5)) / count + jitter * h * 0.04;
      index[li]!.push(nodes.length);
      nodes.push({ x, y, kind: li === 0 ? "shard" : li === layers.length - 1 ? "service" : "hop", glow: 0, bad: 0 });
    }
  });
  const edges: Edge[] = [];
  const edgeOf = new Map<string, number>();
  for (let li = 0; li < layers.length - 1; li++) {
    index[li]!.forEach((a, i) => {
      const next = index[li + 1]!;
      // Each node reaches its nearest one or two in the next layer.
      const ratio = (i + 0.5) / index[li]!.length;
      const near = Math.min(next.length - 1, Math.floor(ratio * next.length));
      for (const j of new Set([near, Math.min(next.length - 1, near + ((i + li) % 2))])) {
        const b = next[j]!;
        const A = nodes[a]!, B = nodes[b]!, mx = (A.x + B.x) / 2;
        edgeOf.set(`${a}-${b}`, edges.length);
        edges.push({ a, b, c1x: mx, c1y: A.y, c2x: mx, c2y: B.y });
      }
    });
  }
  // Every node is reached from somewhere: a node left without an incoming
  // edge takes one from its nearest neighbour in the layer before.
  for (let li = 1; li < layers.length; li++) {
    for (const b of index[li]!) {
      if (edges.some((e) => e.b === b)) continue;
      const B = nodes[b]!;
      const a = index[li - 1]!.reduce((best, n) => Math.abs(nodes[n]!.y - B.y) < Math.abs(nodes[best]!.y - B.y) ? n : best);
      const A = nodes[a]!, mx = (A.x + B.x) / 2;
      edges.push({ a, b, c1x: mx, c1y: A.y, c2x: mx, c2y: B.y });
    }
  }
  // Routes: walks from a shard to a service along the edges.
  const routes: number[][] = [];
  // Walk backwards from every edge's end so each edge is on some route.
  const into = (b: number) => edges.map((e, n) => (e.b === b ? n : -1)).filter((n) => n >= 0);
  const outOf = (a: number) => edges.map((e, n) => (e.a === a ? n : -1)).filter((n) => n >= 0);
  edges.forEach((e, n) => {
    const back: number[] = [];
    let at = e.a;
    while (nodes[at]!.kind !== "shard") { const i = into(at); if (!i.length) return; const pick = i[(n + back.length) % i.length]!; back.unshift(pick); at = edges[pick]!.a; }
    const route = [...back, n];
    at = e.b;
    while (nodes[at]!.kind !== "service") { const o = outOf(at); if (!o.length) return; const pick = o[(n + route.length) % o.length]!; route.push(pick); at = edges[pick]!.b; }
    routes.push(route);
  });
  return { nodes, edges, routes };
}

function point(n: Node[], e: Edge, t: number) {
  const A = n[e.a]!, B = n[e.b]!, u = 1 - t;
  return {
    x: u * u * u * A.x + 3 * u * u * t * e.c1x + 3 * u * t * t * e.c2x + t * t * t * B.x,
    y: u * u * u * A.y + 3 * u * u * t * e.c1y + 3 * u * t * t * e.c2y + t * t * t * B.y,
  };
}

function NetworkCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext?.("2d");
    if (!canvas || !ctx) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    let w = 0, h = 0, graph = build(1, 1), pulses: Pulse[] = [], raf = 0, last = 0, spawn = 0, seq = 0;
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1), r = canvas.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = Math.max(1, Math.round(w * dpr)); canvas.height = Math.max(1, Math.round(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // The graph's shape depends only on its layer counts, so pulses in
      // flight keep their routes when it is laid out again at a new size.
      graph = build(w, h);
      if (still) draw(0);
    };
    const launch = () => {
      const route = graph.routes[seq++ % graph.routes.length]!;
      // About one in six fails somewhere along the way.
      const fails = seq % 6 === 0 ? 1 + (seq % (route.length - 1)) : -1;
      pulses.push({ route, at: 0, speed: 0.55 + ((seq * 37) % 30) / 100, fails, hue: "ok" });
    };
    const draw = (dt: number) => {
      ctx.clearRect(0, 0, w, h);
      const { nodes, edges } = graph;
      ctx.lineWidth = 1.2;
      for (const e of edges) {
        const A = nodes[e.a]!, B = nodes[e.b]!;
        ctx.strokeStyle = COLORS.edge;
        ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.bezierCurveTo(e.c1x, e.c1y, e.c2x, e.c2y, B.x, B.y); ctx.stroke();
      }
      for (const p of pulses) {
        p.at += dt * p.speed;
        const seg = Math.floor(p.at), t = p.at - seg;
        if (seg >= p.route.length) continue;
        if (p.fails >= 0 && seg >= p.fails) {
          const n = nodes[edges[p.route[p.fails]!]!.a]!;
          n.bad = 1; p.at = 99; continue;
        }
        const e = edges[p.route[seg]!]!;
        // A short comet: the head and a fading trail behind it.
        for (let k = 6; k >= 0; k--) {
          const tt = t - k * 0.035;
          if (tt < 0) continue;
          const q = point(nodes, e, tt);
          ctx.fillStyle = COLORS.pulse;
          if (k === 0) { ctx.globalAlpha = 0.25; ctx.beginPath(); ctx.arc(q.x, q.y, 7, 0, Math.PI * 2); ctx.fill(); }
          ctx.globalAlpha = (1 - k / 7) * 0.95;
          ctx.beginPath(); ctx.arc(q.x, q.y, k === 0 ? 3 : 2, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;
        if (t > 0.96) nodes[e.b]!.glow = 1;
      }
      pulses = pulses.filter((p) => p.at < p.route.length);
      for (const n of nodes) {
        const color = n.bad > 0.05 ? COLORS.bad : COLORS[n.kind];
        const r = n.kind === "hop" ? 4 : n.kind === "shard" ? 8 : 5;
        if (n.glow > 0.02 || n.bad > 0.02) {
          const g = Math.max(n.glow, n.bad);
          ctx.fillStyle = color; ctx.globalAlpha = g * 0.25;
          ctx.beginPath(); ctx.arc(n.x, n.y, r + 9 * g, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
        }
        if (n.kind === "shard") {
          // Solid crystal silhouettes distinguish appliances from transit hops,
          // including when a failure turns both symbols the same red.
          ctx.fillStyle = color;
          ctx.beginPath(); ctx.moveTo(n.x, n.y - r); ctx.lineTo(n.x + r, n.y);
          ctx.lineTo(n.x, n.y + r); ctx.lineTo(n.x - r, n.y); ctx.closePath(); ctx.fill();
        } else if (n.kind === "hop") {
          ctx.fillStyle = "#101c32"; ctx.strokeStyle = color; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        } else {
          ctx.fillStyle = color;
          ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, Math.PI * 2); ctx.fill();
        }
        if (n.kind === "service") {
          ctx.strokeStyle = color; ctx.globalAlpha = 0.35; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.arc(n.x, n.y, r + 4, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1;
        }
        n.glow = Math.max(0, n.glow - dt * 1.4);
        n.bad = Math.max(0, n.bad - dt * 0.5);
      }
    };
    const loop = (t: number) => {
      const dt = last ? Math.min(0.05, (t - last) / 1000) : 0;
      last = t;
      spawn -= dt;
      if (spawn <= 0) { launch(); spawn = 0.28 + (seq % 5) * 0.08; }
      draw(dt);
      raf = requestAnimationFrame(loop);
    };
    resize();
    // Open busy: run the network a few seconds before the first frame.
    if (!still) for (let k = 0; k < 90; k++) { spawn -= 0.05; if (spawn <= 0) { launch(); spawn = 0.28 + (seq % 5) * 0.08; } ctx.save(); draw(0.05); ctx.restore(); }
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    observer?.observe(canvas);
    if (!still && typeof requestAnimationFrame === "function") raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); observer?.disconnect(); };
  }, []);
  return <canvas ref={ref} className="auth-canvas" />;
}
